import React, { type ComponentType } from 'react';

/**
 * React.lazy() mit automatischem Retry bei Netzwerk-Fehlern.
 *
 * Problem: Bei schlechtem Netz (mobile Fahrer!) schlägt der Chunk-Download
 * fehl. React.lazy() cached den Fehler → "Erneut versuchen" bringt nichts.
 *
 * Lösung: Bis zu 3 Versuche mit exponentieller Backoff (1s, 2s, 4s).
 *
 * Nutzung:
 *   const MyPage = retryLazy(() => import('./MyPage.jsx'));
 *   // statt: const MyPage = React.lazy(() => import('./MyPage.jsx'));
 *
 * @param importFn - Dynamic import, z.B. `() => import('./Page.jsx')`
 * @param retries  - Max Versuche (Default 3)
 * @returns Lazy-geladene Komponente mit identischer Typisierung wie das Original
 */
export function retryLazy<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries?: number,
): React.LazyExoticComponent<T> {
  const maxRetries = retries || 3;
  return React.lazy(function () {
    return retry(importFn, maxRetries);
  });
}

/**
 * Interner Retry-Helper mit exponentieller Backoff.
 * Generisch — wiederholt einen beliebigen async-Aufruf und reicht das
 * Ergebnis unverändert durch.
 */
function retry<T>(
  fn: () => Promise<T>,
  retriesLeft: number,
  delay?: number,
): Promise<T> {
  const d = delay || 1000;
  return fn().catch(function (err: unknown): Promise<T> {
    if (retriesLeft <= 1) {
      // Letzter Versuch fehlgeschlagen — Fehler weitergeben
      throw err;
    }
    return new Promise<void>(function (resolve) {
      setTimeout(resolve, d);
    }).then(function () {
      return retry(fn, retriesLeft - 1, d * 2);
    });
  });
}
